import React, { createContext, useCallback, useRef, useState } from 'react';
import { MandatoryChildren } from '../models/children-props';

export type PostScheduleProviderProps = MandatoryChildren;

type PostScheduleValueType = {
  loadingInitial: boolean;
  setLoadingInitial: React.Dispatch<React.SetStateAction<boolean>>;
  postsCount: number;
  setPostsCount: React.Dispatch<React.SetStateAction<number>>;
  isMovingPost: boolean;
  setIsMovingPost: React.Dispatch<React.SetStateAction<boolean>>;
  triggerPostReFetch: string;
  setTriggerPostReFetch: React.Dispatch<React.SetStateAction<string>>;
  triggerReShuffleFetch: boolean;
  setTriggerReShuffleFetch: React.Dispatch<React.SetStateAction<boolean>>;
  triggerDeleteReFetch: boolean;
  setTriggerDeleteReFetch: React.Dispatch<React.SetStateAction<boolean>>;
  firstInViewId: string;
  setFirstInViewId: (value: string) => void;
  handlePostVisibilityChange: (isVisible: boolean, postId: string, postIndex: number) => void;
  visiblePostIdsRef: React.MutableRefObject<visiblePostType[]> | null;
  reFetch: boolean;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PostScheduleContext = createContext<PostScheduleValueType>({
  loadingInitial: false,
  setLoadingInitial: () => {
    // ...
  },
  postsCount: 0,
  setPostsCount: () => {
    // ...
  },
  isMovingPost: false,
  setIsMovingPost: () => {
    // ...
  },
  triggerPostReFetch: '',
  setTriggerPostReFetch: () => {
    // ...
  },
  triggerReShuffleFetch: false,
  setTriggerReShuffleFetch: () => {
    // ...
  },
  triggerDeleteReFetch: false,
  setTriggerDeleteReFetch: () => {
    // ...
  },
  firstInViewId: '',
  setFirstInViewId: () => {
    // ...
  },
  handlePostVisibilityChange: () => {
    // ...
  },
  visiblePostIdsRef: null,
  reFetch: false,
  setRefetch: () => {
    // ...
  }
});

type visiblePostType = {
  postId: string;
  index: number;
};

export const PostScheduleProvider = (props: PostScheduleProviderProps): JSX.Element => {
  const { children } = props;
  const [reFetch, setRefetch] = useState<boolean>(false);
  const [triggerPostReFetch, setTriggerPostReFetch] = useState<string>('');
  const [triggerReShuffleFetch, setTriggerReShuffleFetch] = useState<boolean>(false);
  const [triggerDeleteReFetch, setTriggerDeleteReFetch] = useState<boolean>(false);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);
  const [isMovingPost, setIsMovingPost] = useState<boolean>(false);
  const [postsCount, setPostsCount] = useState<number>(0);

  // Using useRef instead of useState for these variables to prevent excessive re-renders
  const firstInViewIdRef = useRef<string>('');
  const visiblePostIdsRef = useRef<visiblePostType[]>([]);

  // Helper function to update the ref value
  const setFirstInViewIdRef = (value: string) => {
    firstInViewIdRef.current = value;
  };

  // adjusts the list of visible posts by adding or removing a post based on its visibility status and provided index
  const handlePostVisibilityChange = useCallback((isVisible: boolean, postId: string, index: number) => {
    const currentVisiblePosts = visiblePostIdsRef.current;

    // Check if an object with the provided postId already exists
    const postExists = currentVisiblePosts.some(post => post.postId === postId);

    // If the post is visible and doesn't exist in the array, add it
    if (isVisible && !postExists) {
      currentVisiblePosts.push({ postId, index });
    }

    // If the post is no longer visible and exists in the array, remove it
    if (!isVisible && postExists) {
      visiblePostIdsRef.current = currentVisiblePosts.filter(post => post.postId !== postId);
    }

    visiblePostIdsRef.current.sort((a, b) => a.index - b.index);
  }, []);

  return (
    <PostScheduleContext.Provider
      value={{
        reFetch,
        setRefetch,
        loadingInitial,
        setLoadingInitial,
        postsCount: postsCount,
        setPostsCount: setPostsCount,
        isMovingPost,
        setIsMovingPost,
        triggerPostReFetch,
        setTriggerPostReFetch,
        triggerReShuffleFetch,
        setTriggerReShuffleFetch,
        triggerDeleteReFetch,
        setTriggerDeleteReFetch,
        firstInViewId: firstInViewIdRef.current, // accessing ref value
        setFirstInViewId: setFirstInViewIdRef, // setting ref value
        handlePostVisibilityChange,
        visiblePostIdsRef
      }}>
      {children}
    </PostScheduleContext.Provider>
  );
};
